import React, { useEffect, useState, useRef } from 'react';
import { clsx } from 'clsx';

// @ts-ignore
import shopify from 'shopify';

import { ProductTags } from './ProductTags/ProductTags';
import { ProductRatings } from '@arctheme-components/ecommerce/Product/ProductRatings/ProductRatings';
import { ProductPrice } from './ProductPrice/ProductPrice';
import { ProductImages } from './ProductImages/ProductImages';

import { ProductSwatches } from '@project-stories/ecommerce/Product/ProductSwatches/ProductSwatches';
import QuickAdd from './ProductTileQuickAdd/ProductTileQuickAdd';
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from '../../../../entrypoints/theme';
import { ProductWishlist } from '@project-stories/ecommerce/Product/ProductWishlist/ProductWishlist';
import { calculatePrices, findMetafield } from 'frontend/js/helpers';
import { decodeHtmlEntities } from "frontend/js/helpers";

declare global {
  interface Window {
      okeWidgetApi: any; 
  }
}
interface ProductTileProps {
  product: any; 
  swatches?: any[];
  siblings?: any[];
  show_add?: boolean;
  show_price_inline?: boolean;
  settings?: any;
  enableWishlist?: boolean;
  showPrice?: boolean;
  showTags?: boolean;
  showQuickAdd?: boolean;
  showDosageButton?: boolean;
  showSubscribeButton?: boolean;
  noSecondaryButton?: boolean;
  productKey?: string;
  type?: 'plp' | 'slider';
  onClickDosage?: () => void;
  onClickSubscribe?: () => void;
}

export const ProductTile = ({
  product,
  swatches = [],
  siblings,
  show_add = false,
  show_price_inline = false,
  settings,
  enableWishlist,
  showPrice = true,
  showTags = true,
  showQuickAdd = true,
  showDosageButton = false, 
  showSubscribeButton = false,
  noSecondaryButton = true,
  productKey = '',
  type = 'plp',
  onClickDosage = () => {},
  onClickSubscribe = () => {}
}: ProductTileProps) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [hasRetrievedMetafields, sethasRetrievedMetafields] = useState(false);
  const [colourName, setColourName] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [noRating, setNoRating] = useState(false);
  const [prices, setPrices] = useState(null);
  const starRatingRef: any = useRef(null);
  const { globals: { translations } } = settings || {};

  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const {
    product_tile_bg_color = '',
    product_tile_sliders_bg_color,
    product_tile_border_radius = '',
    product_tile_border_radius_mobile = '',
    product_tile_center_title = '',
    product_tile_title_style = '',
    product_tile_title_style_mobile = '',
    product_tile_best_value_icon = '',
    product_tile_best_value_label = '',
    product_tile_best_value_label_style = '',
    product_tile_best_value_label_color = '',
  } = settings?.globals?.product_tiles || {};

  const {
    savings_badge_label,
    savings_badge_bundle_label,
    savings_badge_label_style
  } = settings?.globals?.savings_badge || {};

  useEffect(() => {
    async function getMetafieldsAndSwatches() {
      const productMetafields = await shopify.getMetafieldsForProduct([`gid://shopify/Product/${product?.id}`]);

      product.metafields = productMetafields[0]?.metafields;

      let swatchProducts: any = [];

      if (product.metafields){
      for (const metafield of product.metafields) {
        switch (metafield.key) {
          case 'bottle_colour_name':
            setColourName(metafield.value[0]);
            break;
          // case "bottle_colour":
          //   currentColourHex = metafield.value[0];
          //   break;
          case 'bottle_siblings':
            const relatedProductIds = [...metafield.value];

            const relatedProducts = await shopify.getMetafieldsForProduct(relatedProductIds);
            const processedRelatedProducts = new Set();

            relatedProducts.forEach((relatedProduct) => {
              const swatchProduct = {
                url: `/products/${relatedProduct.handle}`, // Assuming `handle` is the URL or can be constructed to form the URL
                color: null,
                image: relatedProduct.image?.src || '', // Assuming `image` field exists and is directly accessible
                name: '' // Assuming `title` is the name
              };

              relatedProduct.metafields.forEach((relatedProductMetafield) => {
                if (relatedProductMetafield.key === 'bottle_colour_name') {
                  swatchProduct.name = relatedProductMetafield.value[0];
                }
                if (relatedProductMetafield.key === 'bottle_colour') {
                  swatchProduct.color = relatedProductMetafield.value[0];
                }
              });
              if (!processedRelatedProducts.has(swatchProduct.name)) {
                swatchProducts.push(swatchProduct);
                processedRelatedProducts.add(swatchProduct.name); // Mark this URL as processed
              }
            });
            break;
        }
      }
    }

      sethasRetrievedMetafields(true);
      setRelatedProducts(swatchProducts);
    }

    if (isHovered && !hasRetrievedMetafields) {
      getMetafieldsAndSwatches();
    }
  }, [isHovered]);

  useEffect(() => {
    async function reinitialiseStarRating () {
      const intervalId = setInterval(async () => {
        if (window?.okeWidgetApi) {
          clearInterval(intervalId);

          await window?.okeWidgetApi?.initWidget(starRatingRef?.current);
    
          setTimeout(() => {
            const hiddenEl = starRatingRef?.current?.querySelector('.okeReviews.oke-sr.oke-sr--hidden');
            setNoRating(!!hiddenEl);
          }, 2000);
        }
      }, 1000);
    };

    reinitialiseStarRating();
  }, [starRatingRef]);

  useEffect(() => {
    async function getPrices() {
      const pricesData = await calculatePrices(product);
      setPrices(pricesData);
    }

    getPrices();
  }, []);

  const createKeyValuePairs = (tags) => {
    const tagsArray = Array.isArray(tags) ? tags : [tags];

    return tagsArray.reduce((acc, tag) => {
      const [key, value] = tag.split(':');
      if (value) {
        if (acc[key]) {
          if (Array.isArray(acc[key])) {
            acc[key].push(value);
          } else {
            acc[key] = [acc[key], value];
          }
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
  };

  const productSwatches: any = [];
  const variantFirstId = product?.variants[0]?.id || product?.id;
  const isAvailable = product?.variants[0]?.available;

  // const showQuickAdd = settings?.globals?.product_tiles?.quickview_add_to_cart_enabled;

  showPrice = settings?.show_price ?? settings?.globals?.show_price;
  showTags = settings?.show_tags ?? true;
  showQuickAdd = settings?.show_quickadd ?? settings?.globals?.product_tiles?.quickview_add_to_cart_enabled;

  const showRating = false;

  let titleClass = product_tile_title_style;

  if (isMobile) {
    titleClass = product_tile_title_style_mobile;
  }

  if (product_tile_center_title) {
    titleClass += ' text-center';
  }

  if (product?.tags) {
    const productTags = createKeyValuePairs(product?.tags);

    if (productTags?.colour) {
      const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];

      colors.forEach((colorHandle) => {
        const siblingProduct = siblings?.find((sibling) => sibling.handle === colorHandle);
        let isProduct = false;
        const matchingProduct = siblings?.find((sibling) => sibling.handle === product?.handle);

        if (matchingProduct === siblingProduct) {
          isProduct = true;
        }
        if (siblingProduct) {
          productSwatches.push({
            url: '/products/' + siblingProduct.handle,
            name: product.title,
            image:
              siblingProduct.images?.find((image) => image.src?.match(/_s_|_s(\.|$)/))?.src ||
              siblingProduct.images[siblingProduct.images?.length - 1].src,
            is_product: isProduct
          });
        }
      });
    }

    if (relatedProducts) {
      const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];

      colors.forEach((colorHandle) => {
        const relatedProduct = relatedProducts?.find((related) => related.handle === colorHandle);
        let isProduct = false;
        const matchingProduct = relatedProducts?.find((related) => related.handle === product.handle);

        if (matchingProduct === relatedProduct) {
          isProduct = true;
        }
        if (relatedProduct) {
          productSwatches.push({
            url: '/products/' + relatedProduct.handle,
            name: product.title,
            image:
              relatedProduct.images?.edges?.find((edge) => edge.node.src?.match(/_s_|_s(\.|$)/))?.node.src ||
              relatedProduct.images?.edges[relatedProduct.images.edges.length - 1]?.node.src,
            is_product: isProduct
          });
        }
      });
    }
  }

  if (product?.images?.length <= 0) {
    product.images.push({
      src: 'https://cdn.shopify.com/s/files/1/0592/8940/3562/files/placeholder.jpg?v=1726554438'
    });
  }
  
  let productUrl = `/products/`;
  if (settings?.collection){
    productUrl = `/collections/${settings.collection?.handle}/products/`;
    // productUrl = `/products/`;
  }

  const bestValue = findMetafield(product?.metafields, 'best-value');

  return (
    <div
      key={`${productKey? productKey : product?.handle}`}
      className={`theme__product-card h-full ${isHovered ? 'theme__product-card--hovered' : ''} ${type == 'slider' ? 'product-card-slider' : ''}`}
      style={{
        backgroundColor: type == 'slider' ?  product_tile_sliders_bg_color : product_tile_bg_color,
        borderRadius: `${isMobile ? product_tile_border_radius_mobile : product_tile_border_radius}px`
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='productcard__content flex flex-col justify-between gap-4'>
        <div className={`flex flex-col ${type == 'plp' ? 'gap-4' : '' } `}>
          {type == 'plp' &&
            <div className="p-2 lg:p-4 pb-0 flex gap-2 items-end flex-wrap flex-col absolute w-full z-10 justify-end">
                  {!!prices?.savings &&
                <div className={`savings-badge ${savings_badge_label_style}`}>
                  <span>
                    {prices.discountType === 'bundle' ? savings_badge_bundle_label : savings_badge_label}
                  </span>
                  <span>{Math.round(prices.savings)}%</span>
                </div>
              }
              <div className="flex gap-2 items-center">
                {bestValue === 'true' &&
                  <div
                    className={`best-value-badge flex gap-2 items-end ${product_tile_best_value_label_style}`}
                    style={{
                      color: product_tile_best_value_label_color
                    }}
                  >
                    <span className={`${product_tile_best_value_icon} text-2xl`}></span>
                    <span>{product_tile_best_value_label}</span>
                  </div>
                }
              </div>

          
            </div>
          }

          {type != 'plp' &&
            <div className={`flex flex-col w-full px-2 lg:px-4 py-2 lg:py-4 items-center h-[56px]`}>
              <a href={'/products/' + product?.handle}>
                <div className={`${titleClass} product-title`}>
                  {product?.title}
                </div>
              </a>
            </div>
          }

          <div className='relative pt-5 md:pt-2 lg:pt-4'>
            <ProductImages
              productUrl={`${productUrl}` + product?.handle}
              product={product}
              isLazy={type == 'slider'}
            />

            {!isMobile && (
              <>
                {showQuickAdd && type !='slider' && (
                  <QuickAdd
                    product={product}
                    isHovered={isHovered}
                  />
                )}

                {enableWishlist && (
                  <div
                    className={`absolute top-0 right-0 transition-opacity duration-300 ${
                      isHovered ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <ProductWishlist
                      product={product}
                      type='plp'
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {showRating && (
            <div className='flex justify-between w-full mt-1 lg:mt-2 max-lg:items-center'>
              <ProductRatings product={product} />
            </div>
          )}

        { type == 'plp' && <div className={`flex flex-col w-full px-2 lg:px-4`}>
            <a href={'/products/' + product?.handle}>
              <div className={`${titleClass}`}>{product?.title}</div>
            </a>
          </div>
        }
        </div>


        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 min-h-[63px] justify-end items-center">
            <div
              ref={starRatingRef}
              data-oke-star-rating
              data-oke-reviews-product-id={`shopify-${product.id}`}
              className={`oke-star-rating-wrapper`}
            ></div>

            <div className={`lg:mt-[5px]`}>
              {showPrice &&
                <ProductPrice
                  product={product}
                  prices={prices}
                />
              }
            </div>
          </div>

          <ajax-cart-product-form className='w-full'>
            <form
              action='/cart/add'
              id='product_form_id'
              className={`product-form flex flex-col px-2 lg:px-7 pb-2 ${(!noSecondaryButton ) && 'min-h-[104px]' }`}
              noValidate
              data-prodify-product-form
            >
              <input type="hidden" name="id" value={`${variantFirstId}`} />

              {isAvailable
                ? <button className={`button button-primary w-full !mt-auto`}>{translations?.add_to_cart}</button>
                : <button className={`button button-tertiary w-full !mt-auto`} disabled>{translations?.out_of_stock}</button>
              }

              <div
                data-ajax-cart-errors='form'
                className='text-xs text-error-1 mt-2'
              ></div>

              { showDosageButton &&  <div className=""> <button  type="button" onClick={onClickDosage} className={`button button-secondary w-full mb-2`}>{translations?.dosage}</button> </div> }
              { showSubscribeButton && product?.bundle && product?.selling_plan_groups.length > 0 && <div className=""> <button  type="button" onClick={onClickSubscribe} className={`button button-secondary w-full mb-2`}>{decodeHtmlEntities(translations?.subscribe) || 'Subscribe & Save'}</button> </div> }
            </form>
          </ajax-cart-product-form>

    

          {/* TODO:
            - Reuse if needed
            - Delete if not

            {product?.selling_plan_groups?.length > 0 && (
            <div
              className={clsx('product__selling-groups overflow-hidden transition-all duration-300', {
                'max-h-0 opacity-0': !isHovered,
                'max-h-96 opacity-100 product-tile-hovered': isHovered
              })}
            >
              <div className='flex flex-col mt-4'>
                {relatedProducts.length > 0 && (
                  <ProductSwatches
                    swatches={relatedProducts}
                    currentColor={colourName}
                    showName={true}
                  />
                )}
                <ProductSellingGroups
                  product={product}
                  showAddToCart={true}
                  isInTile
                />
              </div>
            </div>
          )} */}
        </div>

            
      </div>
    </div>
  );
};

export default ProductTile;
